import * as analytic from '@anm/analytic';
import AddDestinationButton from '@anm/components/stream/destination_management/AddDestinationButton';
import React, { FC } from 'react';
import { NewDestination } from 'types/stream';

import ChooseAccountModal from '../ChooseAccountModal';
import Notification from '../DestinationNotification';
import ErrorNoDestinationsModal from '../ErrorNoDestinationsModal';
import FacebookErrorModal from '../FacebookErrorModal';
import useAddDestination from '../hooks/useAddDestination';
import useAddedDestination from '../hooks/useAddedDestination';
import useChooseAccountModal from '../hooks/useChooseAccountModal';
import useFacebookGroupError from '../hooks/useFacebookGroupError';
import useNoDestinationsModal from '../hooks/useNoDestinationsModal';
import useStorageChange from '../hooks/useStorageChange';
import { Urls } from '../types';

import { WrappedAddIconButton } from './Wrapper';

export type AddDestinationWithModalsProps = Urls & {
  title?: string;
  isError?: boolean;
  isInModal?: boolean;
  showModals?: boolean;
  isUserStaff?: boolean;
  isReconnect?: boolean;
  isTextButton?: boolean;
  onRedirect?(link: string): void;
  renderChildren?(destinations: NewDestination[]): void;
};

const AddDestinationWithModals: FC<AddDestinationWithModalsProps> = ({
  isError,
  waveUrl,
  waveApiUrl,
  showModals = true,
  isInModal = false,
  isTextButton = true,
  isReconnect = false,
  title = 'Add destination',
  onRedirect,
  renderChildren
}) => {
  const [
    network,
    isAdminOnlyGroups,
    canShowChooseAccountModal,
    destinationType,
    filteredDestinations,
    handleCloseChooseAccountModal
  ] = useChooseAccountModal();

  const [isNoDestinationModalOpened, closeNoDestinationsModal] = useNoDestinationsModal({
    destinations: filteredDestinations
  });
  const [canShowErrorModal, closeErrorModal] = useFacebookGroupError();

  const closeModals = () => {
    closeErrorModal();
    handleCloseChooseAccountModal();
  };

  useStorageChange();
  useAddedDestination();

  const [
    addDestinationList,
    renderConnectToFacebookModal,
    facebookGroupModal,
    renderRTMPModal,
    renderAmazonLiveModal
  ] = useAddDestination({
    waveApiUrl,
    isReconnect,
    showModals
  });

  return (
    <>
      {renderRTMPModal()}
      {facebookGroupModal()}
      {renderAmazonLiveModal()}
      {renderConnectToFacebookModal()}
      <Notification {...{ isInModal, waveUrl, onRedirect }} />
      {!!canShowChooseAccountModal && !canShowErrorModal && network && showModals && filteredDestinations && (
        <ChooseAccountModal
          network={network}
          destinations={filteredDestinations}
          destinationType={destinationType}
          onModalClose={handleCloseChooseAccountModal}
        />
      )}
      {isNoDestinationModalOpened && !!destinationType && showModals && (
        <ErrorNoDestinationsModal
          network={network}
          onModalClose={closeNoDestinationsModal}
          type={destinationType}
          isAdminOnlyGroups={isAdminOnlyGroups}
        />
      )}
      {canShowErrorModal && showModals && <FacebookErrorModal onModalClose={closeModals} />}
      {renderChildren?.(addDestinationList) || (
        <AddDestinationButton
          buttonText={isTextButton ? '' : title}
          onSelectDestination={title =>
            analytic.trackMyStreamsSelectDestination(
              title
                .toLowerCase()
                .split(' ')
                .join('_')
            )
          }
          list={addDestinationList}
          {...{ isError }}
        >
          {isTextButton && (
            <WrappedAddIconButton size="large" variant="blue-heavy-border" icon="images/icons/plus_blue.svg">
              {title}
            </WrappedAddIconButton>
          )}
        </AddDestinationButton>
      )}
    </>
  );
};

export default AddDestinationWithModals;
